import { createContext, useCallback, useMemo, useState } from "react";
import { PrescriptionMedicationInstruction } from "src/api/types";
import { useManageModal } from "src/hooks/useManageModal";
import UpdateInstructionsModal from "./components/UpdateInstructionsModal/UpdateInstructionsModal";
import CreateOrderModal from "./components/CreateOrderModal";
import PrescriptionProvider from "../PrescriptionProvider";

type ContextType = {
  openInstructionsModal: (
    details: PrescriptionMedicationInstruction | undefined,
  ) => void;
  openCreateOrderModal: ({
    patientId,
    patientStripeId,
    gender,
  }: {
    patientStripeId: string;
    patientId: number;
    gender: string | null;
  }) => void;
};

export const PatientOrdersModalContext = createContext<ContextType>({
  openInstructionsModal: () => {},
  openCreateOrderModal: () => {},
});

interface Props {
  children: React.ReactNode;
}

const PatientOrdersModalProvider = ({ children }: Props) => {
  const [patientStripeId, setPatientStripeId] = useState<string>("");
  const [patientId, setPatientId] = useState<number | null>(null);
  const [gender, setGender] = useState<string | null>(null);
  const [open, { handleClose, handleOpen }] = useManageModal();
  const [
    openCreate,
    { handleClose: handleCloseCreate, handleOpen: handleOpenCreate },
  ] = useManageModal();

  const [details, setDetails] = useState<
    PrescriptionMedicationInstruction | undefined
  >(undefined);

  const handleOpenInstructionModal = useCallback(
    (details: PrescriptionMedicationInstruction | undefined) => {
      setDetails(details);

      handleOpen();
    },
    [handleOpen],
  );

  const handleCloseInstructionModal = useCallback(() => {
    setDetails(undefined);
    handleClose();
  }, [handleClose]);

  const handleOpenCreateOrderModal = useCallback(
    ({
      patientId,
      patientStripeId,
      gender,
    }: {
      patientStripeId: string;
      patientId: number;
      gender: string | null;
    }) => {
      setPatientStripeId(patientStripeId);
      setPatientId(patientId);
      setGender(gender);
      handleOpenCreate();
    },
    [handleOpenCreate],
  );

  const handleCloseCreateOrderModal = useCallback(() => {
    handleCloseCreate();
  }, [handleCloseCreate]);

  const value = useMemo(
    () => ({
      patientStripeId,
      openInstructionsModal: handleOpenInstructionModal,
      openCreateOrderModal: handleOpenCreateOrderModal,
    }),
    [handleOpenCreateOrderModal, handleOpenInstructionModal, patientStripeId],
  );

  return (
    <PatientOrdersModalContext.Provider value={value}>
      {children}

      {details ? (
        <PrescriptionProvider prescriptionId={details.id}>
          <UpdateInstructionsModal
            open={open}
            onClose={handleCloseInstructionModal}
            details={details}
          />
        </PrescriptionProvider>
      ) : null}

      <CreateOrderModal
        gender={gender}
        open={openCreate}
        patientStripeId={patientStripeId}
        onClose={handleCloseCreateOrderModal}
        patientId={patientId}
      />
    </PatientOrdersModalContext.Provider>
  );
};

export default PatientOrdersModalProvider;
